import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { usePortfolioReportGenerator } from "./usePortfolioReportGenerator";
import {
  DEFAULT_SECTIONS,
  type AnalysisBasis,
  type PortfolioReportOptions,
  type RankingMetric,
  type SectionKey,
  type Segment,
  type TimeBasis,
} from "./types";

interface Props {
  filters: Record<string, string>;
  analysisMode: AnalysisBasis;
  onClose: () => void;
}

const SECTION_LABELS: Record<SectionKey, string> = {
  cover: "Cover",
  executive_summary: "Executive summary",
  fuel_displacement: "Grid generation impact",
  emissions_air: "Avoided emissions — Air",
  emissions_water: "Avoided emissions — Water",
  emissions_soil: "Avoided emissions — Soil",
  emissions_resources: "Avoided resource depletion",
  public_health: "Public Health detail",
  biodiversity: "Biodiversity detail",
  economic: "Economic detail",
  spatial_map: "Spatial impact map",
  ranking_absolute: "Project ranking (absolute)",
  ranking_per_mw: "Project efficiency (per-MW)",
  data_quality: "Data-quality appendix",
  methodology: "Methodology & data sources",
  glossary: "Glossary",
  closing: "Closing",
};

export function PortfolioReportBuilderModal({
  filters,
  analysisMode,
  onClose,
}: Props) {
  const { token } = useAuth();
  const { generate, status, error } = usePortfolioReportGenerator();

  const inferredSegment: Segment =
    filters.lifecycle === "existing"
      ? "operational"
      : filters.lifecycle === "future"
        ? "procurement"
        : "both";

  const [segment, setSegment] = useState<Segment>(inferredSegment);
  const [analysis, setAnalysis] = useState<AnalysisBasis>(analysisMode);
  const [timeBasis, setTimeBasis] = useState<TimeBasis>(
    inferredSegment === "procurement" ? "projection" : "lifetime",
  );
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(thisYear - 1);
  const [lifetimeYears, setLifetimeYears] = useState<number>(25);
  const [sections, setSections] =
    useState<Record<SectionKey, boolean>>(DEFAULT_SECTIONS);
  const [rankingMetric, setRankingMetric] = useState<RankingMetric>("dalys");
  const [topN, setTopN] = useState<number>(25);
  const [title, setTitle] = useState("Portfolio Impact Report");
  const [subtitle, setSubtitle] = useState("");
  const [period, setPeriod] = useState(
    new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" }),
  );
  const [confidential, setConfidential] = useState(true);
  const [pdf, setPdf] = useState(true);
  const [pptx, setPptx] = useState(false);

  const timeBasisOptions = useMemo<{ v: TimeBasis; l: string }[]>(() => {
    if (segment === "procurement")
      return [{ v: "projection", l: "Lifetime projection (forward)" }];
    const opts: { v: TimeBasis; l: string }[] = [
      { v: "lifetime", l: "Lifetime (cumulative realized)" },
      { v: "single_year", l: "Single calendar year" },
    ];
    if (segment === "both")
      opts.push({ v: "projection", l: "Lifetime projection (forward)" });
    return opts;
  }, [segment]);

  const onSegment = (s: Segment) => {
    setSegment(s);
    setTimeBasis(s === "procurement" ? "projection" : "lifetime");
  };

  const busy = status === "loading" || status === "rendering";

  // Esc closes the modal (unless we're mid-generate so the user doesn't
  // accidentally cancel a render).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const run = () => {
    if (!token) return;
    // Fold the segment into the filter set (Operational/Procurement = lifecycle)
    const f = { ...filters };
    if (segment === "operational") f.lifecycle = "existing";
    else if (segment === "procurement") f.lifecycle = "future";
    const options: PortfolioReportOptions = {
      filters: f,
      segment,
      analysis,
      timeBasis,
      year: timeBasis === "single_year" ? year : undefined,
      lifetimeYears: timeBasis === "projection" ? lifetimeYears : undefined,
      sections,
      rankingMetric,
      topN,
      branding: {
        title,
        subtitle,
        period,
        confidential,
        confidentialText: "Confidential",
      },
      outputs: { pdf, pptx },
    };
    generate(token, options);
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => {
        // Click on the backdrop (not the inner panel) closes the modal.
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-surface-700 border border-border p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">
              Build portfolio report
            </h2>
            <p className="text-caption text-text-secondary mt-1">
              {Object.keys(filters).length > 0
                ? `Inherits ${Object.keys(filters).length} active dashboard filter(s)`
                : "Entire portfolio (no filters active)"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-text-muted hover:text-text-primary"
          >
            ✕
          </button>
        </div>

        {/* Scope */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <label className="text-body-sm text-text-secondary">
            Segment
            <select
              value={segment}
              onChange={(e) => onSegment(e.target.value as Segment)}
              className="mt-1 w-full rounded-lg bg-surface-600 px-3 py-2 text-text-primary"
            >
              <option value="operational">Operational (existing)</option>
              <option value="procurement">Procurement (future)</option>
              <option value="both">Both</option>
            </select>
          </label>
          <label className="text-body-sm text-text-secondary">
            Analysis basis
            <select
              value={analysis}
              onChange={(e) => setAnalysis(e.target.value as AnalysisBasis)}
              className="mt-1 w-full rounded-lg bg-surface-600 px-3 py-2 text-text-primary"
            >
              <option value="ctg">Cradle-to-Grave</option>
              <option value="ops">Operational</option>
            </select>
          </label>
          <label className="text-body-sm text-text-secondary">
            Time basis
            <select
              value={timeBasis}
              onChange={(e) => setTimeBasis(e.target.value as TimeBasis)}
              className="mt-1 w-full rounded-lg bg-surface-600 px-3 py-2 text-text-primary"
            >
              {timeBasisOptions.map((o) => (
                <option key={o.v} value={o.v}>
                  {o.l}
                </option>
              ))}
            </select>
          </label>
          {timeBasis === "single_year" && (
            <label className="text-body-sm text-text-secondary">
              Year
              <input
                type="number"
                value={year}
                min={2000}
                max={thisYear}
                onChange={(e) => setYear(Number(e.target.value))}
                className="mt-1 w-full rounded-lg bg-surface-600 px-3 py-2 text-text-primary"
              />
            </label>
          )}
          {timeBasis === "projection" && (
            <label className="text-body-sm text-text-secondary">
              Projection years
              <input
                type="number"
                value={lifetimeYears}
                min={1}
                max={50}
                onChange={(e) => setLifetimeYears(Number(e.target.value))}
                className="mt-1 w-full rounded-lg bg-surface-600 px-3 py-2 text-text-primary"
              />
            </label>
          )}
        </div>

        {/* Branding */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <label className="text-body-sm text-text-secondary col-span-2">
            Title
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 w-full rounded-lg bg-surface-600 px-3 py-2 text-text-primary"
            />
          </label>
          <label className="text-body-sm text-text-secondary">
            Subtitle
            <input
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              className="mt-1 w-full rounded-lg bg-surface-600 px-3 py-2 text-text-primary"
            />
          </label>
          <label className="text-body-sm text-text-secondary">
            Period label
            <input
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="mt-1 w-full rounded-lg bg-surface-600 px-3 py-2 text-text-primary"
            />
          </label>
        </div>

        {/* Sections */}
        <p className="text-body-sm text-text-secondary mb-2">Sections</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-4">
          {(Object.keys(SECTION_LABELS) as SectionKey[]).map((k) => (
            <label
              key={k}
              className="flex items-center gap-2 text-caption text-text-secondary"
            >
              <input
                type="checkbox"
                checked={sections[k]}
                onChange={(e) =>
                  setSections((s) => ({ ...s, [k]: e.target.checked }))
                }
              />
              {SECTION_LABELS[k]}
            </label>
          ))}
        </div>

        {/* Tables + output */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <label className="text-body-sm text-text-secondary">
            Ranking metric
            <select
              value={rankingMetric}
              onChange={(e) =>
                setRankingMetric(e.target.value as RankingMetric)
              }
              className="mt-1 w-full rounded-lg bg-surface-600 px-3 py-2 text-text-primary"
            >
              <option value="dalys">DALYs</option>
              <option value="pdfs">PDFs</option>
              <option value="usd">Economic ($)</option>
            </select>
          </label>
          <label className="text-body-sm text-text-secondary">
            Top N (0 = all)
            <input
              type="number"
              value={topN}
              min={0}
              onChange={(e) => setTopN(Number(e.target.value))}
              className="mt-1 w-full rounded-lg bg-surface-600 px-3 py-2 text-text-primary"
            />
          </label>
        </div>

        <div className="flex items-center gap-4 mb-4 text-body-sm text-text-secondary">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={pdf}
              onChange={(e) => setPdf(e.target.checked)}
            />
            PDF
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={pptx}
              onChange={(e) => setPptx(e.target.checked)}
            />
            PowerPoint (.pptx)
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={confidential}
              onChange={(e) => setConfidential(e.target.checked)}
            />
            Confidential marking
          </label>
        </div>

        {error && (
          <p className="text-caption text-red-400 mb-3">Error: {error}</p>
        )}

        <div className="flex justify-end gap-3">
          <button className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={run}
            disabled={busy || (!pdf && !pptx)}
          >
            {status === "loading"
              ? "Loading data…"
              : status === "rendering"
                ? "Rendering…"
                : status === "done"
                  ? "Done ✓"
                  : "Generate report"}
          </button>
        </div>
      </div>
    </div>
  );
}
